import useMaskedCardNumber from "@/hooks/useMaskedCardNumber";
import {
  numberSeparator,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import BoxKit from "@/shared/components/kit/box";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Divider } from "@mui/material";
import moment from "jalali-moment";
type ContentModalProps = {
  data: {
    amount: number;
    source: string;
    destination: string;
    dateTime: string;
    timestamp: number;
    first_name: string;
    last_name: string;
  };
};
/**
 * ContentModal
 *
 * کامپوننت نمایش جزئیات تراکنش برای نمایش در مودال.
 *
 * ویژگی‌ها:
 * - نمایش مبلغ تراکنش با فرمت هزارگان و ریال
 * - نمایش مبدا و مقصد تراکنش با ماسک کردن شماره کارت
 * - نمایش تاریخ و ساعت تراکنش با تبدیل به تقویم شمسی
 *
 * @param {Object} props
 * @param {ContentModalProps} [props.postAmount] - جزئیات تراکنش
 * @returns JSX.Element
 */
// Task Response:

// بهینه‌سازی و نکات:
// 1. در حال حاضر مبدا و مقصد تراکنش هر دو از `postAmount?.data?.destination` استفاده می‌کنند.
//    احتمالاً اشتباه است و باید `postAmount?.data?.source` برای مبدا استفاده شود.
// 2. استفاده از `moment` برای فرمت تاریخ مناسب است و به شمسی تبدیل می‌شود.
// 3. `useMaskedCardNumber` برای مخفی کردن بخشی از شماره کارت درست استفاده شده است.
// 4. پیشنهاد: می‌توان یک کامپوننت فرزند برای ردیف‌های تراکنش ساخت تا کد تکراری برای Box + Divider + Stack کاهش یابد.
// 5. اطمینان از اینکه `postAmount` موجود باشد قبل از دسترسی به `data` برای جلوگیری از ارور.

const ContentModal = ({ postAmount }: { postAmount?: ContentModalProps }) => {
  return (
    <StackKit
      direction={"column"}
      justifyContent={"space-between"}
      sx={{ width: "100%", height: "75%", paddingTop: "3rem" }}
    >
      <BoxKit
        position="relative"
        sx={{ width: "100%", height: "24px", my: 2, marginBottom: "20px" }}
      >
        <Divider
          sx={{
            position: "absolute",
            width: "60%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderStyle: "dashed",
          }}
        />
        <StackKit
          direction="row"
          justifyContent="space-between"
          sx={{
            position: "relative",
            height: "100%",
            width: "100%",
            zIndex: 1,
            marginBottom: "103px",
          }}
        >
          <TypoGraphyKit
            variant="body1"
            sx={{ px: 1, display: "flex", alignItems: "center" }}
          >
            مبلغ تراکنش
          </TypoGraphyKit>

          <TypoGraphyKit
            variant="body1"
            sx={{ px: 1, display: "flex", alignItems: "center" }}
          >
            {numberSeparator(postAmount?.data?.amount)} ریال
          </TypoGraphyKit>
        </StackKit>
      </BoxKit>
      <BoxKit
        position="relative"
        sx={{ width: "100%", height: "24px", my: 2, marginBottom: "20px" }}
      >
        <Divider
          sx={{
            position: "absolute",
            width: "60%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderStyle: "dashed",
          }}
        />

        <StackKit
          direction="row"
          justifyContent="space-between"
          sx={{
            position: "relative",
            height: "100%",
            width: "100%",
            zIndex: 1,
            marginBottom: "103px",
          }}
        >
          <TypoGraphyKit
            variant="body1"
            sx={{ px: 1, display: "flex", alignItems: "center" }}
          >
            مبدا تراکنش
          </TypoGraphyKit>

          <TypoGraphyKit
            variant="body1"
            sx={{ px: 1, display: "flex", alignItems: "center" }}
            dir="ltr"
          >
            {toPersianNumber(
              useMaskedCardNumber(postAmount?.data?.destination),
            )}
          </TypoGraphyKit>
        </StackKit>
      </BoxKit>
      <BoxKit
        position="relative"
        sx={{ width: "100%", height: "24px", my: 2, marginBottom: "20px" }}
      >
        <Divider
          sx={{
            position: "absolute",
            width: "60%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderStyle: "dashed",
          }}
        />

        <StackKit
          direction="row"
          justifyContent="space-between"
          sx={{
            position: "relative",
            height: "100%",
            width: "100%",
            zIndex: 1,
            marginBottom: "103px",
          }}
        >
          <TypoGraphyKit
            variant="body1"
            sx={{
              px: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            مقصد تراکنش
          </TypoGraphyKit>

          <TypoGraphyKit
            variant="body1"
            sx={{
              px: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            {toPersianNumber(
              useMaskedCardNumber(postAmount?.data?.destination),
            )}
          </TypoGraphyKit>
        </StackKit>
      </BoxKit>
      <BoxKit
        position="relative"
        sx={{ width: "100%", height: "24px", my: 2, marginBottom: "20px" }}
      >
        <Divider
          sx={{
            position: "absolute",
            width: "60%",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderStyle: "dashed",
          }}
        />

        <StackKit
          direction="row"
          justifyContent="space-between"
          sx={{
            position: "relative",
            height: "100%",
            width: "100%",
            zIndex: 1,
            marginBottom: "103px",
          }}
        >
          <TypoGraphyKit
            variant="body1"
            sx={{
              px: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            تاریخ و ساعت
          </TypoGraphyKit>

          <StackKit direction={"row"}>
            <TypoGraphyKit component={"span"}>
              {toPersianNumber(
                moment(postAmount?.data?.timestamp)
                  .locale("fa")
                  .format("HH:MM"),
              )}
            </TypoGraphyKit>
            <TypoGraphyKit
              variant="body1"
              sx={{
                px: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              {toPersianNumber(
                moment(postAmount?.data?.timestamp)
                  .locale("fa")
                  .format("YYYY/MM/DD"),
              )}
            </TypoGraphyKit>
          </StackKit>
        </StackKit>
      </BoxKit>
    </StackKit>
  );
};

export default ContentModal;
